import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@mui/material";

const API_URL = "https://gamecollection-app.onrender.com/api";

export default function SteamLibrary() {
  const [games, setGames] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API_URL}/steam/games`)
      .then((response) => setGames(response.data.games))
      .catch((requestError) => {
        setError(
          requestError.response?.data?.detail ||
            "Unable to load the Steam library."
        );
      })
      .finally(() => setLoading(false));
  }, []);

  const filteredGames = useMemo(() => {
    const query = search.trim().toLowerCase();
    return query
      ? games.filter((game) => game.name.toLowerCase().includes(query))
      : games;
  }, [games, search]);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, display: "flex", gap: 2, flexWrap: "wrap" }}>
      <Box sx={{ flex: "1 1 0", minWidth: 0 }}>
        <Typography variant="h5" component="h1" sx={{ mb: 1 }}>
          Steam Library
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 2 }}>
          <TextField
            label="Search Steam games..."
            variant="outlined"
            fullWidth
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <Typography sx={{ minWidth: "max-content" }}>
            {games.length} {games.length === 1 ? "game" : "games"}
          </Typography>
        </Box>

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Steam data is provided as-is. This app is not affiliated with or endorsed by Valve.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box display="flex" justifyContent="center" my={5}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ overflowY: "auto", maxHeight: "calc(100vh - 180px)", pt: 1, pb: 2, px: 1 }}>
            <Grid container spacing={2} sx={{ overflow: "visible" }}>
              {filteredGames.map((game) => (
                <Grid item xs={6} sm={4} md={3} lg={2} key={game.appid} sx={{ overflow: "visible" }}>
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      aspectRatio: "239 / 340",
                      cursor: "default",
                      borderRadius: 3,
                      boxShadow: 2,
                      border: "1px solid rgba(255,255,255,0.2)",
                      overflow: "hidden",
                      transition: "transform 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        transform: "scale(1.05)",
                        boxShadow: 8,
                        zIndex: 2,
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={`https://cdn.cloudflare.steamstatic.com/steam/apps/${game.appid}/library_600x900.jpg`}
                      alt={`${game.name} cover art`}
                      title={game.name}
                      loading="lazy"
                      decoding="async"
                      onError={(event) => {
                        if (event.currentTarget.dataset.fallback !== "header") {
                          event.currentTarget.dataset.fallback = "header";
                          event.currentTarget.src = `https://cdn.cloudflare.steamstatic.com/steam/apps/${game.appid}/header.jpg`;
                        } else {
                          event.currentTarget.style.display = "none";
                        }
                      }}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                        bgcolor: "grey.900",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        inset: "auto 0 0",
                        background: "linear-gradient(to top, rgba(0,0,0,0.88), rgba(0,0,0,0))",
                        color: "white",
                        px: 1,
                        pt: 3,
                        pb: 1,
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        component="div"
                        sx={{
                          fontWeight: 700,
                          lineHeight: 1.25,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {game.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.8)" }}>
                        {Math.floor(game.playtime_forever / 60)} hrs played
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
            {!error && filteredGames.length === 0 && (
              <Typography color="text.secondary" sx={{ mt: 2 }}>
                {games.length ? "No games match your search." : "No Steam games found."}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
